import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/layout/Layout";
import { Loading } from "@/components/shared/Loading";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import SalesMyDay from "@/pages/sales/MyDay";
import SalesPipeline from "@/pages/sales/Pipeline";
import SalesCustomers from "@/pages/sales/Customers";
import Customer360 from "@/pages/sales/Customer360";
import QuotationDetail from "@/pages/sales/QuotationDetail";
import InquiryRedirect from "@/pages/sales/InquiryRedirect";
import LeadWorkspace from "@/pages/sales/LeadWorkspace";
import OpsMyDay from "@/pages/ops/MyDay";
import OpsPipeline from "@/pages/ops/Pipeline";
import OpsJobs from "@/pages/ops/Jobs";
import JobDetail from "@/pages/ops/JobDetail";
import OpsHandoffReview from "@/pages/ops/HandoffReview";
import FinanceMyDay from "@/pages/finance/MyDay";
import FinanceCredit from "@/pages/finance/Credit";
import FinancePipeline from "@/pages/finance/Pipeline";
import FinanceOrderReview from "@/pages/finance/OrderReview";
import FinancePayables from "@/pages/finance/Payables";
import FinanceReceivables from "@/pages/finance/Receivables";
import FinanceProfitability from "@/pages/finance/Profitability";
import ManagementMyDay from "@/pages/management/MyDay";
import Audit from "@/pages/management/Audit";
import SharedInbox from "@/pages/shared/SharedInbox";
import Approvals from "@/pages/shared/Approvals";
import Exceptions from "@/pages/shared/Exceptions";
import AnalyticsDashboard from "@/pages/analytics/Dashboard";
import Tasks from "@/pages/shared/Tasks";
import Suppliers from "@/pages/shared/Suppliers";

function Protected({ children }) {
  const { auth } = useAuth();
  if (auth === null) return <div className="min-h-screen flex"><Loading label="Signing in" /></div>;
  if (auth === false) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<Protected><Layout /></Protected>}>
                <Route path="/" element={<Navigate to="/sales/inbox" replace />} />
                <Route path="/sales/inbox" element={<SalesMyDay />} />
                {/* Item 1 (Aug 2026 Sales dashboard scope pass) — "Work
                    Queue" removed from the Sales nav; the /sales/tasks
                    route is removed with it (Tasks.jsx and the route still
                    exist and are unchanged for operations/finance/
                    management, which still use their own Work Queue tab). */}
                <Route path="/sales/messages" element={<SharedInbox />} />
                <Route path="/sales/pipeline" element={<SalesPipeline />} />
                <Route path="/sales/customers" element={<SalesCustomers />} />
                <Route path="/sales/suppliers" element={<Suppliers />} />
                <Route path="/sales/customers/:id" element={<Customer360 />} />
                <Route path="/operations/customers/:id" element={<Customer360 />} />
                <Route path="/finance/customers/:id" element={<Customer360 />} />
                <Route path="/management/customers/:id" element={<Customer360 />} />
                <Route path="/sales/quotations/:id" element={<QuotationDetail />} />
                {/* Aug 2026 IA correction: an Inquiry never opens as its own
                    page — this route exists only for deep-linking old
                    bookmarks/notifications and immediately resolves onto the
                    parent customer's page with the inquiry pre-selected. */}
                <Route path="/sales/inquiries/:id" element={<InquiryRedirect />} />
                <Route path="/sales/leads/:id" element={<LeadWorkspace />} />
                {/* Item 6 (Aug 2026 Sales dashboard scope pass) — Job Details
                    must never be shown in the Sales Dashboard; the
                    /sales/jobs/:id route is removed (JobDetail.jsx and the
                    route are unchanged for operations/finance/management). */}
                <Route path="/operations/inbox" element={<OpsMyDay />} />
                {/* Items 2/3 (Aug 2026 Operations round) — "Work Queue"
                    removed from the Operations nav; /operations/tasks stays
                    mounted (Tasks.jsx unchanged) for direct-URL access only,
                    same pattern as the earlier Sales/Finance nav passes.
                    "Pipeline" (Operations-specific stages) replaces it. */}
                <Route path="/operations/tasks" element={<Tasks />} />
                <Route path="/operations/pipeline" element={<OpsPipeline />} />
                <Route path="/operations/messages" element={<SharedInbox />} />
                <Route path="/operations/jobs" element={<OpsJobs />} />
                <Route path="/operations/suppliers" element={<Suppliers />} />
                <Route path="/operations/jobs/:id" element={<JobDetail />} />
                <Route path="/operations/handoffs/:id" element={<OpsHandoffReview />} />
                <Route path="/operations/exceptions" element={<Exceptions />} />
                <Route path="/finance/inbox" element={<FinanceMyDay />} />
                <Route path="/finance/tasks" element={<Tasks />} />
                <Route path="/finance/messages" element={<SharedInbox />} />
                <Route path="/finance/credit" element={<FinanceCredit />} />
                {/* Aug 2026 Finance dashboard scope pass — the Finance
                    sidebar now shows a single "Pipeline" tab in place of
                    the old Work Queue / Supplier Invoice / Supplier
                    Directory / Invoicing & AR tabs (see nav.js). This route
                    and Credit.jsx / Receivables.jsx / Payables.jsx / the
                    shared Suppliers.jsx are left mounted, unchanged, and
                    reachable by direct URL — nothing underlying was
                    deleted, only the nav entries pointing at them. */}
                <Route path="/finance/pipeline" element={<FinancePipeline />} />
                <Route path="/finance/orders/:id" element={<FinanceOrderReview />} />
                <Route path="/finance/jobs/:id" element={<JobDetail />} />
                <Route path="/finance/payables" element={<FinancePayables />} />
                <Route path="/finance/suppliers" element={<Suppliers />} />
                <Route path="/finance/receivables" element={<FinanceReceivables />} />
                <Route path="/finance/approvals" element={<Approvals />} />
                <Route path="/finance/exceptions" element={<Exceptions />} />
                <Route path="/finance/profitability" element={<FinanceProfitability />} />
                <Route path="/management/inbox" element={<ManagementMyDay />} />
                <Route path="/management/tasks" element={<Tasks />} />
                <Route path="/management/approvals" element={<Approvals />} />
                <Route path="/management/exceptions" element={<Exceptions />} />
                <Route path="/management/messages" element={<SharedInbox />} />
                <Route path="/management/audit" element={<Audit />} />
                <Route path="/management/suppliers" element={<Suppliers />} />
                <Route path="/management/jobs/:id" element={<JobDetail />} />
                <Route path="/analytics" element={<AnalyticsDashboard />} />
                <Route path="*" element={<Navigate to="/sales/inbox" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AppProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
